import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type CorrectionResource,
  CorrectionsController_record_v1,
  type CorrectionsController_record_v1Body,
  type OutletResource,
  OutletsController_list_v1,
  type StockLevelResource,
  StockLevelsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCircleCheck,
  IconPlus,
  IconTrash,
  IconWifiOff,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isOffline, isWorthRetrying, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { isoInstantFrom, localInstantOf } from '@/lib/local-instant';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { Screen, ScreenFact, ScreenNote } from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import {
  blankLine,
  correctionSchema,
  type CorrectionValues,
  fingerprintOf,
} from './correction-form';
import {
  CORRECTION_REASONS,
  directionOf,
  directionSays,
  isCorrectionReason,
  quantitySays,
} from './correction-reasons';
import { labelForReason } from './movement-reasons';
import { formatLevel, isBelowZero } from './stock-levels';

/**
 * Something happened to the goods that was not a sale:
 * `POST /v1/outlets/{outletId}/stock/corrections`.
 *
 * ## Nobody types a sign, and that is the screen
 *
 * `quantity` on the wire is a **positive magnitude** and the direction comes
 * from `reason` — `BREAKAGE`, `SPOILAGE` and `LOSS` remove goods, `FOUND` adds
 * them. The API refuses a negative number outright, and the reason it refuses
 * rather than honouring one is worth having in front of whoever changes this
 * form: two spellings of "two fewer" is how a sign error becomes a level nobody
 * can explain. So the quantity field takes digits and nothing else
 * (`correction-form.ts`), and the direction is stated in words beside the reason
 * and again under the lines.
 *
 * **The reason is a picker over a closed set and must never become free text.**
 * Day Close and Reporting total each reason separately, so a typed reason is a
 * quantity nobody can count. The four come from `correction-reasons.ts`, which
 * derives them from the generated body type.
 *
 * ## One key while the body stands, a fresh one when it moves
 *
 * This route is `@Idempotent` and the header is required — without it the
 * request is refused before anything is written. The rule this form needs is
 * neither of the two already in the codebase, and `attemptFor` in
 * `lib/write-attempt.ts` states it at length: the payload here is editable, so a
 * key held for the component's life earns `409 SYNC_IDEMPOTENCY_KEY_REUSED` the
 * first time somebody fixes a typo and saves again, while a key minted per press
 * writes the breakage twice if an answer is ever lost. The key and the body's
 * `id` are minted together, per body, and held for as long as that body stands.
 *
 * ## What the outlet picker offers, and what it leaves out
 *
 * Outlets that carry no stock are not offered. `STOCK_OUTLET_CARRIES_NO_STOCK`
 * is a fact about the place rather than about anything on the form, and a picker
 * that offered one would ask somebody to fill in a correction to be told the
 * place they chose holds no goods. The refusal is still mapped, for an outlet
 * changed while this screen was open.
 *
 * The items come from the outlet's own levels — `GET
 * /v1/outlets/{outletId}/stock`, the same read FE036 renders — which is exactly
 * the set of items that has a level to correct: live availability at this outlet
 * and `tracks_stock` on the item. An item outside it earns
 * `STOCK_ITEM_NOT_TRACKED`, so the picker is what stops that refusal happening
 * at all. It also carries each item's current level, which is the figure the
 * person recording a breakage is standing in front of.
 *
 * **A negative level is shown as it stands** wherever it appears here, per
 * `stock-levels.ts`. Not clamped, not hidden: a correction is one of the acts
 * that answers it.
 */

/** One empty array, so "no items yet" is the same value on every render. */
const NO_ITEMS: readonly StockLevelResource[] = [];

/** Every state the outlet's items can be in. One value, so two cannot coexist. */
type LevelsState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly items: readonly StockLevelResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

export function RecordCorrection() {
  const client = useApiClient();

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any. Without it the notice under an empty picker reads
  // as a statement about the park for as long as the first request takes.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const [levels, setLevels] = useState<LevelsState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires. Bumped after a successful record as
  // well: the levels beside the item picker have just moved.
  const [reloads, setReloads] = useState(0);

  const [formError, setFormError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<CorrectionResource | null>(null);

  /**
   * The key and the id this form is currently attempting under.
   *
   * A ref rather than state: nothing renders from it, and it must be read and
   * written inside one submit without waiting for a render to see the new value.
   */
  const attempt = useRef<WriteAttempt | null>(null);

  // The generic is stated rather than inferred from `defaultValues`, so the
  // control this component hands to `CorrectionLine` below has one type and not
  // one react-hook-form happened to infer.
  const form = useForm<CorrectionValues>({
    resolver: zodResolver(correctionSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId: '',
      reason: '',
      // Prefilled with now, from the injected clock rather than from `new Date()`
      // — the common case is recording what has just been found, and the field
      // stays editable because a breakage noticed on Tuesday that happened on
      // Monday has to be able to say so.
      at: localInstantOf(systemClock),
      lines: [blankLine()],
    },
  });

  const lines = useFieldArray({ control: form.control, name: 'lines' });

  const outletId = useWatch({ control: form.control, name: 'outletId' });
  const reason = useWatch({ control: form.control, name: 'reason' });

  const pending = form.formState.isSubmitting;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired outlets are
    // excluded by the API's own default — a retired outlet has no shelf to
    // correct.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is, rather than replacing a form somebody may be halfway
        // through with an error about a control.
        //
        // Not `outletsAnswered`: a failed read has answered nothing about which
        // outlets carry stock, so the notice keeps the wording that is true
        // either way.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (outletId === '') {
      setLevels({ kind: 'idle' });

      return;
    }

    // Two guards, not one. `controller` stops the request the moment the outlet
    // changes, and `live` stops an answer already in flight from landing after
    // this effect was torn down — without it, switching outlets twice quickly
    // can leave the second outlet's items underneath the first's.
    const controller = new AbortController();
    let live = true;

    setLevels({ kind: 'loading' });

    client
      .request(StockLevelsController_read_v1, {
        path: { outletId },
        // No `asOf`. A correction is recorded against the position now, and the
        // levels shown beside each item are context for the person counting.
        query: {},
        signal: controller.signal,
      })
      .then((stock) => {
        if (live) setLevels({ kind: 'loaded', items: stock.items });
      })
      .catch((error: unknown) => {
        if (live) setLevels({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads]);

  // Only outlets that hold goods. An outlet that carries no stock is refused
  // `STOCK_OUTLET_CARRIES_NO_STOCK` by every one of the three write routes, so
  // offering one would be offering a form that cannot be submitted.
  const stockedOutlets = useMemo(() => outlets.filter((outlet) => outlet.carriesStock), [outlets]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => stockedOutlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [stockedOutlets],
  );

  const reasonItems = useMemo(
    () => CORRECTION_REASONS.map((value) => ({ label: labelForReason(value), value })),
    [],
  );

  const outlet = stockedOutlets.find((candidate) => candidate.id === outletId);

  const items: readonly StockLevelResource[] = levels.kind === 'loaded' ? levels.items : NO_ITEMS;

  // Derived from `levels` rather than from `items` above, which is the same set
  // read through a conditional and would therefore be a fresh array on every
  // render — a memo that never hits.
  const itemOptions = useMemo(
    () =>
      (levels.kind === 'loaded' ? levels.items : NO_ITEMS).map((item) => ({
        label: `${item.code} — ${item.name}`,
        value: item.catalogueItemId,
      })),
    [levels],
  );

  // Nothing to correct: the outlet stocks no item that counts stock. Said where
  // the lines are rather than as a refusal after the form is filled in.
  const nothingToCorrect = levels.kind === 'loaded' && items.length === 0;

  const chosenReason = isCorrectionReason(reason) ? reason : null;

  const lineErrors = form.formState.errors.lines;
  const linesMessage = lineErrors?.root?.message ?? lineErrors?.message;

  /**
   * A refusal, put where the thing it is about is.
   *
   * The copy is never written here. `messageForFailure` turns the code into a
   * sentence and is the only thing that does; what this decides is *which field*
   * the sentence belongs to, which is the half a screen holding the lines can
   * answer and the API cannot.
   */
  const recordFailure = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'STOCK_OUTLET_CARRIES_NO_STOCK') {
      // A fact about the place. The picker does not offer such an outlet, so
      // this is one changed while the screen was open.
      form.setError('outletId', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'STOCK_REASON_INVALID') {
      form.setError('reason', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'STOCK_ITEM_NOT_TRACKED') {
      // The refusal carries the ids it is about in `details`, and the error map
      // deliberately does not name them — "a screen holding the lines knows
      // which one it sent and says so itself". This is that screen. Anything it
      // cannot match falls through to the form-level message below, so a
      // `details` shape that changes loses the marker rather than the message.
      const named = namedItemIds(error);
      const marked = form.getValues('lines').reduce((count, line, index) => {
        if (!named.has(line.catalogueItemId)) return count;

        form.setError(`lines.${index}.catalogueItemId`, {
          message: messageForFailure(error),
        });

        return count + 1;
      }, 0);

      if (marked > 0) return;
    }

    setFormError(messageForFailure(error));
  };

  const handleSubmit = async (values: CorrectionValues) => {
    // Belt and braces with the disabled button below. This route *adds*
    // movements to an append-only table, so a second submit that got past both
    // is a second breakage nobody can take back.
    if (pending) return;

    // The resolver has already refused everything these two can be null for.
    // Read as impossible states rather than asserted away with a `!`: a change
    // to the schema that let a bad value through shows as a field that refuses
    // rather than as a body carrying `undefined`.
    const occurredAt = isoInstantFrom(values.at);

    if (occurredAt === null) {
      form.setError('at', { message: 'That is not a real date and time.' });

      return;
    }

    if (!isCorrectionReason(values.reason)) {
      form.setError('reason', { message: 'Choose a reason from the list.' });

      return;
    }

    setFormError(null);
    setRecorded(null);

    // Everything but the id, which is minted with the key below because the two
    // together are what makes one attempt one write.
    const content = {
      occurredAt,
      reason: values.reason,
      lines: values.lines.map((line) => ({
        catalogueItemId: line.catalogueItemId,

        // A plain magnitude. The schema has already refused anything that is not
        // digits, so this cannot be negative and cannot be fractional — and
        // nothing here applies a sign, because the sign is the server's to
        // derive from `reason`.
        quantity: Number(line.quantity),
      })),
    };

    const sending = attemptFor(attempt.current, fingerprintOf(values.outletId, content));

    attempt.current = sending;

    try {
      // The body's shape comes from the generated client. Nothing about this
      // route's path or payload is written down in this file.
      const body: CorrectionsController_record_v1Body = { id: sending.id, ...content };

      const answer = await client.request(CorrectionsController_record_v1, {
        path: { outletId: values.outletId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      setRecorded(answer);

      // The correction is written, so the next one is a different act however
      // similar it looks — two breakages of the same item on the same day are
      // two real corrections and must not replay the first one's answer.
      attempt.current = null;

      // The outlet is kept, because somebody recording one correction is often
      // recording two at the same place. The moment is refreshed rather than
      // left at the old one, which would quietly backdate the next record.
      form.reset({
        outletId: values.outletId,
        reason: '',
        at: localInstantOf(systemClock),
        lines: [blankLine()],
      });

      // The levels beside the item picker have just moved. Re-read them rather
      // than adjusting them here: the level is the server's arithmetic over its
      // own log (FR-507), and a screen that did the sum itself would be a second
      // opinion about a number that has one.
      setReloads((count) => count + 1);
    } catch (error: unknown) {
      // Nothing is cleared. The retry is usually one corrected field, and
      // re-typing a whole correction to fix it is a cruelty this form is in no
      // position to inflict. The attempt is deliberately left in place too — if
      // the body is unchanged, pressing again replays under the same key.
      recordFailure(error);
    }
  };

  return (
    <Screen
      title="Record a correction"
      description="Goods broken, spoiled, lost or found — anything that changed what is on the shelf and was not a sale or a delivery. The reason decides which way the level moves; you enter plain counts."
      // Only when there is something standing to say. Every fact on this bar is
      // conditional — the two navigation links that used to hold it up moved to
      // the margin — and a `Screen` given an `aside` draws the band whether or
      // not the fragment inside it renders anything, which on a form nobody has
      // touched yet is an empty ruled strip under the head.
      aside={
        outlet === undefined && chosenReason === null && levels.kind !== 'loaded' ? undefined : (
          <>
            {outlet !== undefined && (
              <ScreenFact label="Outlet">
                <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
                {isRetired(outlet) && (
                  <>
                    {' '}
                    <Badge variant="outline">Retired</Badge>
                  </>
                )}
              </ScreenFact>
            )}

            {chosenReason !== null && (
              <ScreenFact label="This correction">
                {labelForReason(chosenReason)} — {directionSays(chosenReason)}.
                <br />
                <span className="text-muted-foreground">
                  {directionOf(chosenReason) === 'removes'
                    ? 'Every line lowers the outlet’s level by the count you enter.'
                    : 'Every line raises the outlet’s level by the count you enter.'}
                </span>
              </ScreenFact>
            )}

            {levels.kind === 'loaded' && (
              <ScreenFact label="Items stocked">
                <span className="font-mono numeric">{items.length}</span>{' '}
                {items.length === 1 ? 'item' : 'items'}
                <br />
                <span className="text-muted-foreground">
                  What this outlet stocks and can have corrected.
                </span>
              </ScreenFact>
            )}
          </>
        )
      }
      // The standing reference beside the fields, and the whole of what says
      // "this screen is a form". Both notes are this screen's own help text
      // re-homed: a sentence that says what to type stays under its field, one
      // that says what the thing *is* reads better once, beside the form, than
      // repeated under three controls somebody is tabbing through.
      guidance={
        <>
          <ScreenNote label="The reason decides">
            One reason for the whole correction, from the published list. It is what decides the
            direction, and Day Close totals each reason separately.
          </ScreenNote>

          <ScreenNote label="Two clocks">
            The time you enter is recorded as the human fact; the server keeps its own clock for
            ordering and for which day this belongs to.
          </ScreenNote>

          {/* The related screens, at the foot of the margin behind a rule. They
              were in the `aside` bar, which is filters and counts — a
              navigation link is neither, and two of them beside a day picker
              read as controls on the field. */}
          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <Link
              to={paths.stock}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              What this outlet holds
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
            <br />
            <Link
              to={paths.stockMovements}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              Every movement, newest first
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {recorded !== null && <Recorded correction={recorded} />}

      <Form {...form}>
        {/* One column, per docs/ui-conventions.md. `noValidate` because the
              browser's own bubbles are not this app's error language. */}
        <form
          className="space-y-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="outletId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Outlet
                </FormLabel>
                {/* Not wrapped in `FormControl`: the trigger is a button
                      rather than the input the generated id belongs on. */}
                <Select
                  value={field.value}
                  items={outletItems}
                  onValueChange={(next) => {
                    // Base UI types the value as nullable because a select may
                    // be cleared. `''` is what this form calls "nothing
                    // chosen", and the schema refuses it.
                    field.onChange(next ?? '');

                    // The lines named items stocked by the *previous* outlet.
                    // Left standing they would be sent against a place that
                    // may not stock them at all — a `RESOURCE_NOT_FOUND` or a
                    // correction against the wrong shelf. Cleared, and said
                    // plainly rather than silently: the item picker below
                    // empties with them.
                    lines.replace([blankLine()]);
                    setRecorded(null);
                    setFormError(null);
                  }}
                  disabled={stockedOutlets.length === 0}
                >
                  <SelectTrigger
                    id="correction-outlet"
                    aria-label="Outlet"
                    aria-invalid={form.formState.errors.outletId !== undefined}
                    aria-describedby={
                      form.formState.errors.outletId === undefined
                        ? undefined
                        : 'correction-outlet-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose an outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {stockedOutlets.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormDescription>
                  {outletsAnswered && stockedOutlets.length === 0
                    ? 'No outlet in reach carries stock, so there is no level here to correct. An owner sets an outlet up to hold goods.'
                    : 'Only outlets that hold goods are listed. Stock is corrected one outlet at a time.'}
                </FormDescription>
                <FormMessage id="correction-outlet-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Reason
                </FormLabel>
                <Select
                  value={field.value}
                  items={reasonItems}
                  onValueChange={(next) => {
                    field.onChange(next ?? '');
                  }}
                >
                  <SelectTrigger
                    id="correction-reason"
                    aria-label="Reason"
                    aria-invalid={form.formState.errors.reason !== undefined}
                    aria-describedby={
                      form.formState.errors.reason === undefined
                        ? undefined
                        : 'correction-reason-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {CORRECTION_REASONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {/* The label the movement log will show, and beside
                                it the thing that actually differs between these
                                four: which way the level moves. */}
                          <span className="flex items-baseline gap-2">
                            {labelForReason(option)}
                            <span className="text-xs text-muted-foreground">
                              {directionSays(option)}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage id="correction-reason-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="at"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  When it happened
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      // A native date-and-time field rather than a box asking
                      // for ISO 8601, and typed in the zone every instant in
                      // this app is rendered in.
                      type="datetime-local"
                      className={`${fieldSurface} h-11 w-full numeric`}
                      required
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Filled in with now. Change it for something noticed today that happened earlier.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <fieldset className="space-y-4 border-t border-border pt-6">
            <legend className="sr-only">Items corrected</legend>

            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-foreground">What was affected</p>
              <p className="text-sm text-muted-foreground">
                {chosenReason === null
                  ? 'Plain counts. The reason decides the direction.'
                  : quantitySays(chosenReason)}
              </p>
            </div>

            {levels.kind === 'idle' && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                Choose an outlet first. The items it stocks are what can be corrected, and they come
                with what the records say is on its shelf.
              </p>
            )}

            {levels.kind === 'loading' && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                Reading what this outlet holds…
              </p>
            )}

            {levels.kind === 'failed' && (
              <ItemsFailed error={levels.error} onRetry={() => setReloads((count) => count + 1)} />
            )}

            {nothingToCorrect && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                This outlet stocks no item that counts stock, so there is no level here to correct.
                An owner makes an item available at an outlet, under where it sells.
              </p>
            )}

            {levels.kind === 'loaded' &&
              items.length > 0 &&
              lines.fields.map((line, index) => (
                <CorrectionLine
                  key={line.id}
                  index={index}
                  control={form.control}
                  items={items}
                  itemOptions={itemOptions}
                  // The only line on the form is not removable: a correction
                  // with no lines is refused, and a control that empties the
                  // form to a state it cannot be submitted from is a control
                  // that only leads somewhere wrong.
                  onRemove={lines.fields.length > 1 ? () => lines.remove(index) : null}
                />
              ))}

            {levels.kind === 'loaded' && items.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => lines.append(blankLine())}
                // One line per item, and the outlet has as many items as it
                // has. Past that every further line would name an item already
                // on the form, which is the one refusal this epic expects
                // most.
                disabled={lines.fields.length >= items.length}
              >
                <IconPlus data-icon="inline-start" />
                Add another item
              </Button>
            )}

            {/* The array's own message — "add at least one item", "too many
                  lines" — has no field to sit beside, so it sits under the set
                  it is about. Read from `root` first and from the array node
                  second, because react-hook-form puts an array-level error in
                  either place depending on who set it, and rendering both would
                  print the same sentence twice. */}
            {linesMessage !== undefined && (
              <p className="text-sm text-destructive">{linesMessage}</p>
            )}
          </fieldset>

          {/* Above the submit and it stays there — a toast vanishes, and
                somebody who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            // The one primary action on the screen, and the only control that
            // writes anything. Disabled while in flight, which is what stops a
            // second movement for one breakage.
            disabled={pending || nothingToCorrect || levels.kind !== 'loaded'}
          >
            {pending ? 'Recording…' : 'Record correction'}
          </Button>
        </form>
      </Form>
    </Screen>
  );
}

/**
 * One line: which item, and how many.
 *
 * Stacked below `sm` and side by side above it. `docs/ui-conventions.md` asks
 * for one column, and the reason it gives is that a second column fails at a
 * till and on a narrow window — so the rule is kept where it bites: this is one
 * column of *lines*, and a line becomes one column the moment the window is
 * narrow enough for it to matter. The form itself is never two columns.
 */
function CorrectionLine({
  index,
  control,
  items,
  itemOptions,
  onRemove,
}: {
  index: number;
  control: Control<CorrectionValues>;
  items: readonly StockLevelResource[];
  itemOptions: readonly { label: string; value: string }[];
  onRemove: (() => void) | null;
}) {
  const chosenId = useWatch({ control, name: `lines.${index}.catalogueItemId` });
  const chosen = items.find((item) => item.catalogueItemId === chosenId);

  return (
    <div className="space-y-4 rounded-2xl border border-border p-4 sm:flex sm:items-start sm:gap-4 sm:space-y-0">
      <FormField
        control={control}
        name={`lines.${index}.catalogueItemId`}
        render={({ field }) => (
          <FormItem className="sm:flex-1">
            <FormLabel required className={fieldLabel}>
              Item
            </FormLabel>
            <Select
              value={field.value}
              items={itemOptions}
              onValueChange={(next) => {
                field.onChange(next ?? '');
              }}
            >
              <SelectTrigger
                aria-label={`Item on line ${String(index + 1)}`}
                className={`${fieldSurface} h-11 w-full`}
              >
                <SelectValue placeholder="Choose an item" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {items.map((item) => (
                    <SelectItem key={item.catalogueItemId} value={item.catalogueItemId}>
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono numeric">{item.code}</span>
                        {item.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {chosen !== undefined && (
              <FormDescription>
                {/* What the records say is there now — the figure the person is
                    standing in front of a shelf comparing against. A negative
                    one is shown as it stands: it means the records owe the
                    shelf, and it is a reason to count rather than a reason to
                    hide the row. */}
                Records say <span className="font-mono numeric">{formatLevel(chosen.level)}</span>{' '}
                on the shelf
                {isBelowZero(chosen.level) && ' — below zero already, which a stocktake settles'}.
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`lines.${index}.quantity`}
        render={({ field }) => (
          <FormItem className="sm:w-32">
            <FormLabel required className={fieldLabel}>
              How many
            </FormLabel>
            <FormControl
              render={
                <Input
                  // `inputMode` and not `type="number"`, which hands the browser
                  // its own opinion — a spinner, a wheel that changes the count
                  // under a pointer, and a value that reads back empty as `NaN`.
                  // A count is read back and typed in, so it gets the mono face
                  // and tabular figures.
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="2"
                  className={`${fieldSurface} h-11 w-full font-mono numeric`}
                  required
                  {...field}
                />
              }
            />
            <FormMessage />
          </FormItem>
        )}
      />

      {onRemove !== null && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="sm:mt-7"
          aria-label={`Remove line ${String(index + 1)}`}
        >
          <IconTrash aria-hidden />
        </Button>
      )}
    </div>
  );
}

/**
 * The outlet's items could not be read.
 *
 * Not `LoadFailed`, which words itself as a list that could not be loaded and
 * sits inside a table. This is a form that cannot be filled in yet, and offline
 * is said as the operating mode it is rather than as a fault.
 */
function ItemsFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <Alert>
      {isOffline(error) ? <IconWifiOff /> : <IconAlertTriangle />}
      <AlertTitle>
        {isOffline(error)
          ? 'No connection to the server'
          : 'What this outlet stocks is unavailable'}
      </AlertTitle>
      <AlertDescription>
        <span>{messageForFailure(error)}</span>
        {isWorthRetrying(error) && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
            Try again
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * What was just recorded, echoed from the server's answer rather than from what
 * was sent.
 *
 * The difference matters on exactly one field: `quantity` comes back as
 * submitted, and the *sign* is not in the response at all — it is in the
 * movement each line wrote. So this states the direction in words from the
 * reason the server echoed, which is the same thing the level did.
 */
function Recorded({ correction }: { correction: CorrectionResource }) {
  const reason = correction.reason;

  return (
    // `status` and not the component's default `alert`: a correction that was
    // recorded is not an interruption.
    <Alert role="status">
      <IconCircleCheck />
      <AlertTitle>Recorded</AlertTitle>
      <AlertDescription>
        <span>
          {labelForReason(reason)} — {directionSays(reason)}.{' '}
          <span className="font-mono numeric">{String(correction.lines.length)}</span>{' '}
          {correction.lines.length === 1 ? 'item' : 'items'}, and the levels have moved. It is in
          the movement log now, and it is not undone by editing — a further correction is how a
          mistake here is put right.
        </span>
      </AlertDescription>
    </Alert>
  );
}

/**
 * The catalogue items a refusal names, if it names any.
 *
 * `details` is the API's own envelope field and is typed as unknown values, so
 * every step is checked: a shape that changes upstream loses the per-line marker
 * and falls back to the message above the form, rather than throwing inside a
 * catch block.
 */
function namedItemIds(error: ApiError): ReadonlySet<string> {
  const named = error.details?.['catalogueItemIds'];

  if (!Array.isArray(named)) return new Set();

  return new Set(named.filter((value): value is string => typeof value === 'string'));
}
